import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import {
    Assessment,
    Close,
    CloudDownload,
    Email,
    Search,
    StarBorder,
    WhatsApp,
} from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect, useState } from "react"
import {
    exportarAvaliacoesCSV,
    obterDetalhesResposta,
    type DetalhesResposta,
    type ItemPesquisa,
} from "../../../services/pesquisaSatisfacao"
import { PesquisaSatisfacaoFornecedorActionsTypes } from "../context/pesquisaSatisfacaoFornecedorActions"
import { usePesquisaSatisfacaoFornecedorContext } from "../context/usePesquisaSatisfacaoFornecedorContext"

const LIKERT_LABEL: Record<number, string> = {
    0: "Ótimo",
    1: "Bom",
    2: "Regular",
    3: "Ruim",
    4: "Péssimo",
}

const LIKERT_COR: Record<number, string> = {
    0: "#4caf50",
    1: "#8bc34a",
    2: "#ffc107",
    3: "#ff9800",
    4: "var(--cor-erro)",
}

function msParaInputDate(ms: number): string {
    const d = new Date(ms)
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const dd = String(d.getDate()).padStart(2, "0")
    return `${yyyy}-${mm}-${dd}`
}

function inputDateParaMs(value: string): number {
    return new Date(value + "T00:00:00").getTime()
}

function GraficoBarraHorizontal({ item }: { item: ItemPesquisa }) {
    const total = item.otimo + item.bom + item.regular + item.ruim + item.pessimo
    if (total === 0) {
        return (
            <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                Sem respostas
            </Typography>
        )
    }
    const segmentos: { label: string; valor: number; cor: string }[] = [
        { label: "Ótimo", valor: item.otimo, cor: "#4caf50" },
        { label: "Bom", valor: item.bom, cor: "#8bc34a" },
        { label: "Regular", valor: item.regular, cor: "#ffc107" },
        { label: "Ruim", valor: item.ruim, cor: "#ff9800" },
        { label: "Péssimo", valor: item.pessimo, cor: "var(--cor-erro)" },
    ]
    return (
        <Box>
            <Box display="flex" borderRadius={4} overflow="hidden" height={28} mb={1}>
                {segmentos.map(
                    s =>
                        s.valor > 0 && (
                            <Box
                                key={s.label}
                                title={`${s.label}: ${s.valor}`}
                                style={{
                                    width: `${(s.valor / total) * 100}%`,
                                    backgroundColor: s.cor,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minWidth: s.valor > 0 ? 24 : 0,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    style={{ color: "white", fontWeight: 600, fontSize: 11 }}
                                >
                                    {s.valor}
                                </Typography>
                            </Box>
                        )
                )}
            </Box>
            <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                {segmentos.map(s => (
                    <Box key={s.label} display="flex" alignItems="center" style={{ gap: 4 }}>
                        <Box
                            width={10}
                            height={10}
                            borderRadius={2}
                            style={{ backgroundColor: s.cor, flexShrink: 0 }}
                        />
                        <Typography variant="caption" style={{ color: "#7f8c8d" }}>
                            {s.label}: {s.valor}
                        </Typography>
                    </Box>
                ))}
                <Typography variant="caption" style={{ color: "#7f8c8d" }}>
                    | Total: {total}
                </Typography>
            </Box>
        </Box>
    )
}

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "960px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    pesquisarButton: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        fontWeight: 600,
        boxShadow: "0 4px 15px rgba(var(--cor-botao-salvar-rgb),0.3)",
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
    },
    exportButton: {
        borderRadius: 25,
        borderColor: "#4caf50",
        color: "#4caf50",
        fontWeight: 600,
        "&:hover": { backgroundColor: "rgba(var(--cor-botao-salvar-rgb),0.05)" },
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap" as const,
    },
    textField: {
        "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            "& fieldset": { borderColor: "#e0e0e0" },
            "&:hover fieldset": { borderColor: "#4caf50" },
            "&.Mui-focused fieldset": { borderColor: "#4caf50" },
        },
    },
}))

export function ResultadoPesquisa() {
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const { dados, dispatch, pesquisar, erroCarregamento } = usePesquisaSatisfacaoFornecedorContext()
    const { enqueueSnackbar } = useSnackbar()

    const [inicioStr, setInicioStr] = useState(() => msParaInputDate(dados.inicioMs))
    const [fimStr, setFimStr] = useState(() => msParaInputDate(dados.fimMs))
    const [dialogAberto, setDialogAberto] = useState(false)
    const [detalhes, setDetalhes] = useState<DetalhesResposta | null>(null)
    const [carregandoDetalhes, setCarregandoDetalhes] = useState(false)
    const [exportando, setExportando] = useState(false)

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
    }, [erroCarregamento])

    function isDataValida(str: string): boolean {
        if (!str || str.length !== 10) return false
        const d = new Date(str + "T00:00:00")
        if (isNaN(d.getTime())) return false
        const [yyyy, mm, dd] = str.split("-").map(Number)
        return d.getFullYear() === yyyy && d.getMonth() + 1 === mm && d.getDate() === dd
    }

    const handlePesquisar = async () => {
        if (!isDataValida(inicioStr)) {
            alertStart("Data de início inválida.", "error")
            return
        }
        if (!isDataValida(fimStr)) {
            alertStart("Data de fim inválida.", "error")
            return
        }
        const inicioMs = inputDateParaMs(inicioStr)
        const fimMs = inputDateParaMs(fimStr)
        dispatch({ type: PesquisaSatisfacaoFornecedorActionsTypes.ATUALIZAR_INICIO, payload: inicioMs })
        dispatch({ type: PesquisaSatisfacaoFornecedorActionsTypes.ATUALIZAR_FIM, payload: fimMs })
        await pesquisar(inicioMs, fimMs)
    }

    const handleVerDetalhes = async (idRespostaQuestionario: number) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setCarregandoDetalhes(true)
        setDialogAberto(true)
        setDetalhes(null)
        const response = await obterDetalhesResposta(autorizacao, idRespostaQuestionario)
        setCarregandoDetalhes(false)
        if ("retornoErro" in response) {
            alertStart(response.mensagem || "Erro ao carregar detalhes", "error")
            setDialogAberto(false)
        } else {
            setDetalhes(response)
        }
    }

    const handleExportar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setExportando(true)
        const response = await exportarAvaliacoesCSV(autorizacao, dados.inicioMs, dados.fimMs)
        setExportando(false)
        if ("retornoErro" in response) {
            alertStart(response.mensagem || "Erro ao exportar", "error")
            return
        }
        if (response.length === 0) {
            alertStart("Nenhuma avaliação no período selecionado.", "info")
            return
        }
        const cabecalho = Object.keys(response[0]).join(";")
        const linhas = response.map(row => Object.values(row).join(";")).join("\n")
        const csvContent = "﻿" + cabecalho + "\n" + linhas
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
        const url = URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.download = "avaliacoes.csv"
        link.click()
        URL.revokeObjectURL(url)
    }

    const linkWhatsapp = (telefone: string | null, nomeCliente: string, estabelecimento: string) => {
        if (!telefone) return ""
        const tel = "55" + telefone.replace(/[\s\-().+]/g, "")
        const texto = encodeURIComponent(`Olá ${nomeCliente}! Obrigado pela sua avaliação.  ${estabelecimento}`)
        return `https://api.whatsapp.com/send?phone=${tel}&text=${texto}`
    }

    return (
        <div className={classes.container}>
            <div className={classes.content}>
                <Box mb={4} display="flex" justifyContent="space-between" alignItems="center"
                    flexWrap="wrap" style={{ gap: 8 }}>
                    <Box>
                        <Typography variant="h4" className={classes.titleText}>
                            Pesquisa de Satisfação
                        </Typography>
                        <Typography variant="subtitle1" style={{ color: "#7f8c8d" }}>
                            Resultado das avaliações do seu estabelecimento
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        className={classes.exportButton}
                        startIcon={<CloudDownload />}
                        onClick={handleExportar}
                        disabled={exportando}
                        size={isMobile ? "small" : "medium"}
                    >
                        {exportando ? "Exportando..." : "Exportar CSV"}
                    </Button>
                </Box>

                {/* Filtro de datas */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Search className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Filtrar por período
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Box display="flex" flexWrap="wrap" style={{ gap: 16 }} alignItems="flex-end">
                            <TextField
                                className={classes.textField}
                                label="Data Início"
                                type="date"
                                variant="outlined"
                                size="small"
                                value={inicioStr}
                                onChange={e => setInicioStr(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                style={{ minWidth: 160 }}
                            />
                            <TextField
                                className={classes.textField}
                                label="Data Fim"
                                type="date"
                                variant="outlined"
                                size="small"
                                value={fimStr}
                                onChange={e => setFimStr(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                style={{ minWidth: 160 }}
                            />
                            <Button
                                className={classes.pesquisarButton}
                                startIcon={<Search />}
                                onClick={handlePesquisar}
                                size="medium"
                            >
                                Pesquisar
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Gráficos por pergunta */}
                {dados.itens.length > 0 && (
                    <Paper className={classes.configCard}>
                        <Box className={classes.cardHeader}>
                            <Assessment className={classes.iconHeader} />
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                Resultado por Pergunta
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent}>
                            {dados.itens.map((item, idx) => (
                                <Box key={idx} mb={idx < dados.itens.length - 1 ? 3 : 0}>
                                    <Typography
                                        variant="subtitle2"
                                        style={{ fontWeight: 600, color: "#2c3e50", marginBottom: 8 }}
                                    >
                                        {item.pergunta}
                                    </Typography>
                                    <GraficoBarraHorizontal item={item} />
                                    {idx < dados.itens.length - 1 && (
                                        <Divider style={{ marginTop: 16 }} />
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Paper>
                )}

                {/* Tabela de respostas individuais */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <StarBorder className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Respostas Individuais
                        </Typography>
                        <Typography
                            variant="body2"
                            style={{ color: "#7f8c8d", marginLeft: "auto" }}
                        >
                            {dados.total} registro(s)
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        {dados.comentarios.length === 0 ? (
                            <Typography
                                style={{ color: "#7f8c8d", textAlign: "center", padding: "32px 0" }}
                            >
                                Nenhuma resposta encontrada no período selecionado.
                            </Typography>
                        ) : (
                            <TableContainer style={{ overflowX: "auto" }}>
                                <Table size="small" style={{ width: "100%", minWidth: 800 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ minWidth: 160 }}>
                                                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                                                    Cliente
                                                </Typography>
                                            </TableCell>
                                            {!isMobile && (
                                                <TableCell style={{ minWidth: 180 }}>
                                                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                                                        E-mail
                                                    </Typography>
                                                </TableCell>
                                            )}
                                            <TableCell style={{ minWidth: 200 }}>
                                                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                                                    Comentário
                                                </Typography>
                                            </TableCell>
                                            {!isMobile && (
                                                <TableCell style={{ minWidth: 120 }}>
                                                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                                                        Data
                                                    </Typography>
                                                </TableCell>
                                            )}
                                            <TableCell
                                                style={{
                                                    minWidth: 140,
                                                    whiteSpace: "nowrap" as const,
                                                }}
                                                align="center"
                                            >
                                                Ações
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dados.comentarios.map(c => (
                                            <TableRow key={c.idRespostaQuestionario}>
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={c.cliente}
                                                >
                                                    {c.cliente}
                                                </TableCell>
                                                {!isMobile && (
                                                    <TableCell
                                                        className={classes.nomeCell}
                                                        title={c.email}
                                                    >
                                                        {c.email}
                                                    </TableCell>
                                                )}
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={c.sugestao ?? ""}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        style={{ color: "#555" }}
                                                    >
                                                        {c.sugestao || "—"}
                                                    </Typography>
                                                </TableCell>
                                                {!isMobile && (
                                                    <TableCell>
                                                        <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                                                            {c.dataFormatada}
                                                        </Typography>
                                                    </TableCell>
                                                )}
                                                <TableCell
                                                    align="center"
                                                    style={{ whiteSpace: "nowrap" as const }}
                                                >
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        style={{
                                                            backgroundColor: "#ffc107",
                                                            color: "white",
                                                            marginRight: 4,
                                                            minWidth: 0,
                                                            padding: "2px 8px",
                                                            fontSize: 11,
                                                        }}
                                                        onClick={() =>
                                                            handleVerDetalhes(c.idRespostaQuestionario)
                                                        }
                                                    >
                                                        Detalhes
                                                    </Button>
                                                    {c.telefone && (
                                                        <IconButton
                                                            size="small"
                                                            component="a"
                                                            href={linkWhatsapp(
                                                                c.telefone,
                                                                c.cliente,
                                                                c.estabelecimento
                                                            )}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ color: "#25d366", padding: 4 }}
                                                        >
                                                            <WhatsApp fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                    <IconButton
                                                        size="small"
                                                        component="a"
                                                        href={`mailto:${c.email}?Subject=Resposta%20a%20sua%20avalia%C3%A7%C3%A3o`}
                                                        style={{ color: "#2196f3", padding: 4 }}
                                                    >
                                                        <Email fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>
            </div>

            {/* Dialog de detalhes */}
            <Dialog
                open={dialogAberto}
                onClose={() => setDialogAberto(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" style={{ fontWeight: 600, color: "#2c3e50" }}>
                            Detalhes da Avaliação
                        </Typography>
                        <IconButton size="small" onClick={() => setDialogAberto(false)}>
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {carregandoDetalhes && (
                        <Typography style={{ color: "#7f8c8d", textAlign: "center", padding: 16 }}>
                            Carregando...
                        </Typography>
                    )}
                    {detalhes && (
                        <Box>
                            <Typography
                                variant="subtitle1"
                                style={{ fontWeight: 600, color: "#2c3e50", marginBottom: 4 }}
                            >
                                Dados do Cliente
                            </Typography>
                            <Typography variant="body2">
                                <strong>Nome:</strong> {detalhes.cliente}
                            </Typography>
                            <Typography variant="body2">
                                <strong>E-mail:</strong> {detalhes.email}
                            </Typography>
                            {detalhes.telefone && (
                                <Typography variant="body2">
                                    <strong>Telefone:</strong> {detalhes.telefone}
                                </Typography>
                            )}

                            <Divider style={{ margin: "16px 0" }} />

                            <Typography
                                variant="subtitle1"
                                style={{ fontWeight: 600, color: "#2c3e50", marginBottom: 8 }}
                            >
                                Respostas
                            </Typography>
                            {detalhes.respostas.map((r, idx) => (
                                <Box key={idx} mb={2}>
                                    <Typography
                                        variant="body2"
                                        style={{ fontWeight: 600, color: "#2c3e50" }}
                                    >
                                        {r.pergunta}
                                    </Typography>
                                    {r.likert !== null ? (
                                        <Box display="flex" alignItems="center" mt={0.5}>
                                            <Box
                                                width={12}
                                                height={12}
                                                borderRadius={3}
                                                style={{
                                                    backgroundColor: LIKERT_COR[r.likert] ?? "#ccc",
                                                    marginRight: 6,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                style={{
                                                    color: LIKERT_COR[r.likert] ?? "#555",
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {LIKERT_LABEL[r.likert] ?? `Nota ${r.likert}`}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" style={{ color: "#555", marginTop: 4 }}>
                                            {r.respostaTexto || "—"}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    )
}
